import {
  addDays,
  differenceInCalendarDays,
  eachDayOfInterval,
  eachMonthOfInterval,
  eachWeekOfInterval,
  endOfDay,
  format,
  startOfDay,
  startOfMonth,
  startOfQuarter,
  startOfWeek,
  startOfYear,
  subMonths,
} from 'date-fns';

/**
 * Pure calculations behind /crm/marketing-reports. Kept out of the page so the
 * numbers can be unit-tested and reasoned about in one place.
 *
 * Data notes (verified against production, Oct 2026):
 * - Leads and clients share the `accounts` collection and conversion flips
 *   `type` in place, so "leads generated" must count BOTH types — counting only
 *   type === 'Leads' silently drops every lead that became a client.
 * - Daily website (GA4) and ad (Meta) rows are pushed by webhooks the morning
 *   after; `date` is the reported day at UTC midnight, so it must be read with
 *   UTC components or US time zones shift every row back a day.
 * - GA4 bounceRate / engagementRate are stored as 0–1 fractions.
 * - Social follower rows are snapshots, never sums.
 */

export const TIMEFRAMES = [
  { key: 'month', label: 'This Month' },
  { key: 'quarter', label: 'This Quarter' },
  { key: 'year', label: 'This Year' },
  { key: '12m', label: 'Last 12 Months' },
  { key: 'all', label: 'All Time' },
];

export const LEAD_STAGE_ORDER = [
  'Prospects',
  'Lead Received',
  'Discovery Call',
  'Assessment',
  'Onboarding Call',
  'Proposal',
];

export const SOCIAL_PLATFORMS = ['Facebook', 'Instagram', 'LinkedIn', 'YouTube', 'TikTok'];

// ── Date parsing ────────────────────────────────────────────────────────────

/** Firestore Timestamp / Date / ISO string / millis → Date (or null). */
export const toDate = (v) => {
  if (!v) return null;
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : v;
  if (typeof v.toDate === 'function') return v.toDate();
  if (typeof v === 'object' && typeof v.seconds === 'number') return new Date(v.seconds * 1000);
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v)) {
    const [y, m, d] = v.split('-').map(Number);
    return new Date(y, m - 1, d);
  }
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
};

/**
 * Calendar day a daily report row describes, as local midnight. Webhook rows
 * store the day at UTC midnight; rows without a date fall back to the day
 * before they were written (the sync runs the following morning).
 */
export const reportDay = (entry) => {
  const raw = entry.date || entry.reportStartDate;
  if (typeof raw === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(raw)) return toDate(raw);
  const d = toDate(raw);
  if (d) return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  const created = toDate(entry.createdAt);
  return created ? startOfDay(addDays(created, -1)) : null;
};

// ── Ranges & buckets ────────────────────────────────────────────────────────

/**
 * Current window + equal-length previous window for period-over-period deltas.
 * `earliest` anchors All Time (no previous window there).
 */
export const getRange = (key, now = new Date(), earliest = null) => {
  const end = endOfDay(now);
  let start;
  switch (key) {
    case 'month': start = startOfMonth(now); break;
    case 'quarter': start = startOfQuarter(now); break;
    case 'year': start = startOfYear(now); break;
    case '12m': start = startOfMonth(subMonths(now, 11)); break;
    default: start = startOfMonth(earliest || subMonths(now, 11));
  }
  if (key === 'all') return { start, end, prevStart: null, prevEnd: null };
  const spanDays = differenceInCalendarDays(end, start) + 1;
  const prevEnd = endOfDay(addDays(start, -1));
  const prevStart = startOfDay(addDays(start, -spanDays));
  return { start, end, prevStart, prevEnd };
};

export const inRange = (d, start, end) => !!d && d >= start && d <= end;

/** Day buckets up to ~6 weeks, week buckets up to ~6 months, months beyond. */
export const bucketUnit = (start, end) => {
  const days = differenceInCalendarDays(end, start) + 1;
  if (days <= 45) return 'day';
  if (days <= 190) return 'week';
  return 'month';
};

const bucketStart = (d, unit) => {
  if (unit === 'day') return startOfDay(d);
  if (unit === 'week') return startOfWeek(d, { weekStartsOn: 1 });
  return startOfMonth(d);
};

const bucketLabel = (d, unit, multiYear) => {
  if (unit === 'month') return format(d, multiYear ? 'MMM yy' : 'MMM');
  return format(d, 'MMM d');
};

/** Empty, ordered bucket skeleton covering the whole window (zero-filled). */
export const buildBuckets = (start, end, unit) => {
  const interval = { start, end };
  const starts = unit === 'day'
    ? eachDayOfInterval(interval)
    : unit === 'week'
      ? eachWeekOfInterval(interval, { weekStartsOn: 1 })
      : eachMonthOfInterval(interval);
  const multiYear = start.getFullYear() !== end.getFullYear();
  return starts.map((s) => ({
    key: s.getTime(),
    label: bucketLabel(s, unit, multiYear),
    tooltipLabel: unit === 'day'
      ? format(s, 'EEE, MMM d, yyyy')
      : unit === 'week'
        ? `Week of ${format(s, 'MMM d, yyyy')}`
        : format(s, 'MMMM yyyy'),
  }));
};

export const bucketKey = (d, unit) => bucketStart(d, unit).getTime();

// ── Leads ───────────────────────────────────────────────────────────────────

const LOST_STAGES = new Set(['lost', 'archive', 'archived']);

/** Normalise one account doc into the fields the report needs. */
export const normalizeLead = (acc) => {
  const isClient = acc.type === 'Clients';
  const stage = (acc.stage || '').trim();
  // Lost/Archive on a *client* means churn — they still converted, so they
  // count as clients and show as "Former client", not as a lost lead.
  const inLostStage = LOST_STAGES.has(stage.toLowerCase());
  const lost = !isClient && inLostStage;
  return {
    id: acc.id,
    name: (acc.company && acc.company !== 'Unknown' ? acc.company : '') || acc.email || acc.company || 'Unnamed lead',
    createdAt: toDate(acc.createdAt),
    source: (acc.leadSource || '').trim(),
    stage: isClient ? (inLostStage ? 'Former client' : 'Client') : (stage || 'Lead Received'),
    isClient,
    lost,
    // A client was qualified by definition; otherwise only explicit flags count.
    qualified: isClient || acc.qualified === true,
  };
};

// Channel roll-up for the "By channel" view. Order = legend order.
const CHANNEL_RULES = [
  { channel: 'Website', test: /^(website|web|google|seo|organic)/i },
  { channel: 'Referral', test: /referr?al/i },
  { channel: 'Paid Social', test: /(meta ads|facebook ads|paid)/i },
  { channel: 'Social', test: /(facebook|instagram|linkedin|tiktok|youtube|reddit|social)/i },
  { channel: 'Podcast', test: /podcast/i },
  { channel: 'Events', test: /(bootcamp|workshop|conference|summit|ibs|sebc|expo|show|\b\d{2}\b)/i },
  { channel: 'JobTread', test: /jobtread/i },
];

export const channelFor = (source) => {
  if (!source) return 'Unattributed';
  const rule = CHANNEL_RULES.find((r) => r.test.test(source));
  return rule ? rule.channel : 'Other';
};

/**
 * Case-insensitive grouping so "Jobtread" and "JobTread" are one row; the
 * display name is whichever spelling is used most.
 */
export const groupLeadsBySource = (leads, mode = 'source') => {
  const groups = new Map();
  leads.forEach((l) => {
    const raw = mode === 'channel' ? channelFor(l.source) : (l.source || 'Unattributed');
    const k = raw.toLowerCase();
    if (!groups.has(k)) groups.set(k, { spellings: {}, leads: 0, qualified: 0, clients: 0, lost: 0 });
    const g = groups.get(k);
    g.spellings[raw] = (g.spellings[raw] || 0) + 1;
    g.leads += 1;
    if (l.qualified) g.qualified += 1;
    if (l.isClient) g.clients += 1;
    if (l.lost) g.lost += 1;
  });
  const total = leads.length || 1;
  return [...groups.values()]
    .map((g) => {
      const name = Object.entries(g.spellings).sort((a, b) => b[1] - a[1])[0][0];
      const open = g.leads - g.clients - g.lost;
      return {
        name,
        leads: g.leads,
        qualified: g.qualified,
        clients: g.clients,
        lost: g.lost,
        open,
        share: g.leads / total,
        qualifiedRate: g.leads ? g.qualified / g.leads : 0,
        conversionRate: g.leads ? g.clients / g.leads : 0,
      };
    })
    .sort((a, b) => b.leads - a.leads || a.name.localeCompare(b.name));
};

export const summarizeLeads = (leads) => {
  const total = leads.length;
  const qualified = leads.filter((l) => l.qualified).length;
  const clients = leads.filter((l) => l.isClient).length;
  const lost = leads.filter((l) => l.lost).length;
  return {
    total,
    qualified,
    clients,
    lost,
    open: total - clients - lost,
    qualifiedRate: total ? qualified / total : 0,
    conversionRate: total ? clients / total : 0,
  };
};

export const leadSeries = (leads, start, end, unit) => {
  const buckets = buildBuckets(start, end, unit).map((b) => ({ ...b, leads: 0, qualified: 0, clients: 0 }));
  const index = new Map(buckets.map((b, i) => [b.key, i]));
  leads.forEach((l) => {
    const i = index.get(bucketKey(l.createdAt, unit));
    if (i === undefined) return;
    buckets[i].leads += 1;
    if (l.qualified) buckets[i].qualified += 1;
    if (l.isClient) buckets[i].clients += 1;
  });
  return buckets;
};

export const stageBreakdown = (leads) => {
  const counts = {};
  leads.forEach((l) => {
    const k = l.lost ? 'Lost' : l.stage;
    counts[k] = (counts[k] || 0) + 1;
  });
  const order = [...LEAD_STAGE_ORDER, 'Client', 'Former client', 'Lost'];
  const extra = Object.keys(counts).filter((k) => !order.includes(k)).sort();
  return [...order, ...extra]
    .filter((k) => counts[k])
    .map((stage) => ({ stage, count: counts[stage] }));
};

// ── Daily report rows (website + ads) ───────────────────────────────────────

const num = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

/**
 * One row per calendar day (the webhook can re-post a day); the most recently
 * written row wins.
 */
export const dedupeDaily = (entries) => {
  const byDay = new Map();
  entries.forEach((e) => {
    const day = reportDay(e);
    if (!day) return;
    const k = day.getTime();
    const prev = byDay.get(k);
    const written = toDate(e.updatedAt) || toDate(e.createdAt) || new Date(0);
    if (!prev || written > prev.written) byDay.set(k, { ...e, day, written });
  });
  return [...byDay.values()].sort((a, b) => a.day - b.day);
};

export const summarizeWebsite = (rows) => {
  const visitors = rows.reduce((s, r) => s + num(r.visitors), 0);
  const sessions = rows.reduce((s, r) => s + num(r.sessions), 0);
  const engaged = rows.reduce((s, r) => s + num(r.engagedSessions), 0);
  const newUsers = rows.reduce((s, r) => s + num(r.newUsers), 0);
  const views = rows.reduce((s, r) => s + num(r.views), 0);
  // Session-weighted so a 2-session day doesn't count as much as a 40-session day.
  const durationWeighted = rows.reduce((s, r) => s + num(r.sessionDuration) * num(r.sessions), 0);
  return {
    visitors,
    sessions,
    engagedSessions: engaged,
    newUsers,
    views,
    engagementRate: sessions ? engaged / sessions : 0,
    bounceRate: sessions ? 1 - engaged / sessions : 0,
    avgSessionDuration: sessions ? durationWeighted / sessions : 0,
    viewsPerSession: sessions ? views / sessions : 0,
    days: rows.length,
  };
};

export const websiteSeries = (rows, start, end, unit) => {
  const buckets = buildBuckets(start, end, unit).map((b) => ({ ...b, visitors: 0, sessions: 0, newUsers: 0, views: 0 }));
  const index = new Map(buckets.map((b, i) => [b.key, i]));
  rows.forEach((r) => {
    const i = index.get(bucketKey(r.day, unit));
    if (i === undefined) return;
    buckets[i].visitors += num(r.visitors);
    buckets[i].sessions += num(r.sessions);
    buckets[i].newUsers += num(r.newUsers);
    buckets[i].views += num(r.views);
  });
  return buckets;
};

export const adSpendOf = (r) => num(r.adSpend) || num(r.spend);

export const summarizeAds = (rows) => {
  const spend = rows.reduce((s, r) => s + adSpendOf(r), 0);
  const impressions = rows.reduce((s, r) => s + num(r.impressions), 0);
  const clicks = rows.reduce((s, r) => s + num(r.clicks), 0);
  const conversions = rows.reduce((s, r) => s + num(r.conversions), 0);
  return {
    spend,
    impressions,
    clicks,
    conversions,
    ctr: impressions ? clicks / impressions : 0,
    cpc: clicks ? spend / clicks : 0,
    cpm: impressions ? (spend / impressions) * 1000 : 0,
    activeDays: rows.filter((r) => adSpendOf(r) > 0).length,
  };
};

export const adSeries = (rows, start, end, unit) => {
  const buckets = buildBuckets(start, end, unit).map((b) => ({ ...b, spend: 0, clicks: 0, impressions: 0 }));
  const index = new Map(buckets.map((b, i) => [b.key, i]));
  rows.forEach((r) => {
    const i = index.get(bucketKey(r.day, unit));
    if (i === undefined) return;
    buckets[i].spend += adSpendOf(r);
    buckets[i].clicks += num(r.clicks);
    buckets[i].impressions += num(r.impressions);
  });
  return buckets.map((b) => ({ ...b, spend: Math.round(b.spend * 100) / 100 }));
};

// ── Social snapshots ────────────────────────────────────────────────────────

const socialDay = (e) => toDate(e.date) || (e.month && e.year ? new Date(`${e.month} 1, ${e.year}`) : null) || toDate(e.createdAt);

/**
 * Latest follower count per platform as of `asOf`, plus the change since the
 * latest snapshot on/before `since` (or the earliest snapshot in the window).
 */
export const socialSummary = (entries, since, asOf) => {
  return SOCIAL_PLATFORMS.map((platform) => {
    const snaps = entries
      .filter((e) => e.platform === platform)
      .map((e) => ({ day: socialDay(e), followers: parseInt(e.followers, 10) || 0 }))
      .filter((s) => s.day && s.day <= asOf)
      .sort((a, b) => a.day - b.day);
    if (!snaps.length) return { platform, followers: null, change: null, asOf: null, snapshots: 0 };
    const latest = snaps[snaps.length - 1];
    let baseline = null;
    if (since) {
      const before = snaps.filter((s) => s.day <= since);
      baseline = before.length ? before[before.length - 1] : snaps.find((s) => s.day >= since) || null;
    }
    const change = baseline && baseline !== latest ? latest.followers - baseline.followers : null;
    return { platform, followers: latest.followers, change, asOf: latest.day, snapshots: snaps.length };
  });
};

/** One row per snapshot date with each platform's latest known count carried forward. */
export const socialSeries = (entries) => {
  const snaps = entries
    .map((e) => ({ day: socialDay(e), platform: e.platform, followers: parseInt(e.followers, 10) || 0 }))
    .filter((s) => s.day && SOCIAL_PLATFORMS.includes(s.platform))
    .sort((a, b) => a.day - b.day);
  const days = [...new Set(snaps.map((s) => startOfDay(s.day).getTime()))];
  const current = {};
  let i = 0;
  return days.map((t) => {
    while (i < snaps.length && startOfDay(snaps[i].day).getTime() === t) {
      current[snaps[i].platform] = snaps[i].followers;
      i += 1;
    }
    return { key: t, label: format(new Date(t), 'MMM d, yy'), ...current };
  });
};
